import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TelegramLinkButton } from './TelegramLinkButton';
import { TelegramAccountStatus } from '@shared/types';
import { getTelegramStatus, updateTelegramNotifications } from '@/lib/api';

export function TelegramSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: status, isLoading } = useQuery<TelegramAccountStatus>({
    queryKey: ['telegramStatus'],
    queryFn: getTelegramStatus,
    retry: false,
  });

  const updateNotificationsMutation = useMutation({
    mutationFn: updateTelegramNotifications,
    onMutate: async (enabled: boolean) => {
      await queryClient.cancelQueries({ queryKey: ['telegramStatus'] });
      const previousStatus = queryClient.getQueryData<TelegramAccountStatus>(['telegramStatus']);
      if (previousStatus) {
        queryClient.setQueryData<TelegramAccountStatus>(['telegramStatus'], {
          ...previousStatus,
          notificationsEnabled: enabled,
        });
      }
      return { previousStatus };
    },
    onSuccess: (_data, enabled) => {
      toast({
        title: enabled ? 'Notifications enabled' : 'Notifications disabled',
        description: enabled
          ? 'You will receive betting tips via Telegram'
          : 'You will no longer receive Telegram notifications',
      });
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousStatus) {
        queryClient.setQueryData(['telegramStatus'], context.previousStatus);
      }
      toast({
        title: 'Error',
        description: error.message || 'Failed to update notification settings',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['telegramStatus'] });
    },
  });

  const handleNotificationToggle = (checked: boolean) => {
    updateNotificationsMutation.mutate(checked);
  };

  return (
    <Card className="glass">
      <CardHeader>
        <CardTitle>Telegram Integration</CardTitle>
        <CardDescription>
          Connect your Telegram account to receive tips and notifications
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">Loading Telegram settings...</span>
          </div>
        ) : (
          <>
            <TelegramLinkButton />

            {status?.linked && (
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                     <Label htmlFor="telegram-notifications">
                       Enable Telegram Notifications
                     </Label>
                     <p className="text-sm text-muted-foreground">
                       Receive betting tips and updates via Telegram
                     </p>
                  </div>
                  <Switch
                    id="telegram-notifications"
                    checked={status?.notificationsEnabled ?? false}
                    onCheckedChange={handleNotificationToggle}
                    disabled={updateNotificationsMutation.isPending}
                  />
                </div>
              </div>
            )}

            {status?.linked && (
              <div className="pt-2">
                <p className="text-sm text-muted-foreground">
                  Join our Telegram channels to receive tips:
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  <li className="text-muted-foreground">
                    • Free Tips: Check your Telegram for channel link
                  </li>
                  {status?.linkMode === 'primary' && (
                    <li className="text-muted-foreground">
                      • Premium Tips: Exclusive invite link sent on upgrade
                    </li>
                  )}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
